'use client';

import { useState, useRef, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { ChevronDown, Check, Briefcase, ShoppingBag, Headphones, Store, Loader2 } from 'lucide-react';
import { ActorType } from '@hummii/shared-types';
import { cn } from '@/src/shared/lib/utils';
import { switchOrCreateActorByType } from '@/src/entities/user/api';

interface RoleSwitcherProps {
  activeRole: ActorType;
  onRoleChange: (role: ActorType) => void;
  locale: string;
}

/**
 * RoleSwitcher - Uber-style dropdown for switching between user actors
 * Always shows CLIENT and WORKER options (auto-creates actor if doesn't exist)
 *
 * ⚠️ Migration Note: Updated from UserRole to ActorType
 * - CONTRACTOR → WORKER
 * - Auto-creates missing actors on switch
 */
export function RoleSwitcher({
  activeRole,
  onRoleChange,
  locale,
}: RoleSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const t = useTranslations('header.roleSwitcher');

  // Always show CLIENT and WORKER options
  const availableRoles = [ActorType.CLIENT, ActorType.WORKER];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getRoleIcon = (role: ActorType) => {
    switch (role) {
      case ActorType.WORKER:
        return Briefcase;
      case ActorType.CLIENT:
        return ShoppingBag;
      case ActorType.SUPPORT:
        return Headphones;
      case ActorType.STORE:
        return Store;
      default:
        return ShoppingBag;
    }
  };

  const getRoleLabel = (role: ActorType) => {
    switch (role) {
      case ActorType.WORKER:
        return t('worker') || t('contractor') || 'Worker Mode';
      case ActorType.CLIENT:
        return t('client') || 'Client Mode';
      case ActorType.SUPPORT:
        return t('support') || 'Support';
      case ActorType.STORE:
        return t('store') || 'Store';
      default:
        return role;
    }
  };

  const handleRoleSwitch = async (role: ActorType) => {
    if (role === activeRole || isSwitching) return;

    setIsSwitching(true);
    try {
      const response = await switchOrCreateActorByType(role);
      
      // Call parent callback to update active role
      onRoleChange(role);
      
      setIsOpen(false);
    } catch (error) {
      console.error('[RoleSwitcher] Failed to switch role:', error);
      // Keep dropdown open on error so user can try again
    } finally {
      setIsSwitching(false);
    }
  };

  const ActiveIcon = getRoleIcon(activeRole);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isSwitching}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors',
          'text-desktop-body-sm font-medium',
          'hover:bg-background-2',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          isOpen && 'bg-background-2'
        )}
      >
        {isSwitching ? (
          <Loader2 className="h-5 w-5 text-accent-1 animate-spin" />
        ) : (
          <ActiveIcon className="h-5 w-5 text-accent-1" />
        )}
        <span className="text-text-primary">{getRoleLabel(activeRole)}</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-text-secondary transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={cn(
            'absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-border-primary',
            'py-2 z-50'
          )}
        >
          {availableRoles.map((role) => {
            const Icon = getRoleIcon(role);
            const isActive = activeRole === role;

            return (
              <button
                key={role}
                onClick={() => handleRoleSwitch(role)}
                disabled={isSwitching || isActive}
                className={cn(
                  'w-full px-4 py-3 text-left transition-colors',
                  'flex items-center gap-3',
                  'hover:bg-background-2',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  isActive && 'bg-accent-1/10'
                )}
              >
                <Icon
                  className={cn(
                    'h-5 w-5',
                    isActive ? 'text-accent-1' : 'text-text-secondary'
                  )}
                />
                <span
                  className={cn(
                    'flex-1 text-desktop-body-sm font-medium',
                    isActive ? 'text-accent-1' : 'text-text-primary'
                  )}
                >
                  {getRoleLabel(role)}
                </span>
                {isActive && <Check className="h-5 w-5 text-accent-1" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
